import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import ClientViewListingsMap from "../../clientViewRuntime/components/ClientViewListingsMap";
import {
  fetchCasaMapaPartnerMarketAreas,
  fetchCasaMapaPartnerMarketHeatmap,
} from "../../services/casamapaPartnerAgencyApi";
import { CASAMAPA_CLIENT_VIEW_SLUG } from "../../constants/casamapaPartnerPortalConfig";
import {
  CASAMAPA_DEFAULT_CENTER,
  CASAMAPA_DEFAULT_ZOOM,
} from "../../utils/casamap/casaMapaSearchParams";
import { useLocale } from "../../intl";
import {
  findBoundaryFeature,
  normalizeBoundaryId,
} from "../../clientViewRuntime/utils/boundaryHighlight";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerCasaMapaMarketTools.css";

const COPY = "realEstate.casaMapa.admin.partnerHeatmap";

function boundsFromGeojson(geojson) {
  const features = geojson?.features || [];
  if (!features.length) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  const visit = (coords) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      const [lng, lat] = coords;
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
      return;
    }
    coords.forEach(visit);
  };

  features.forEach((feature) => visit(feature?.geometry?.coordinates));
  if (![west, south, east, north].every(Number.isFinite)) return null;
  return { west, south, east, north };
}

export default function PartnerCasaMapaHeatmapPage() {
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const [municipality, setMunicipality] = useState("");
  const [areas, setAreas] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [selectedBarrio, setSelectedBarrio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchCasaMapaPartnerMarketAreas()
      .then(setAreas)
      .catch(() => setAreas({ municipalities: [] }));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerMarketHeatmap({
        days: 30,
        municipality: municipality || undefined,
      });
      setHeatmap(result.heatmap || null);
      setSelectedBarrio(null);
    } catch (err) {
      setHeatmap(null);
      setError(err?.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [municipality, t]);

  useEffect(() => {
    load();
  }, [load]);

  const barrios = heatmap?.barrios || [];
  const boundaryGeojson = heatmap?.boundaryGeojson || null;
  const initialBounds = useMemo(() => boundsFromGeojson(boundaryGeojson), [boundaryGeojson]);
  const polygonCount = boundaryGeojson?.features?.length || 0;
  useEffect(() => {
    const node = document.querySelector(".cm-partner-heatmap__list > li.is-selected");
    node?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedBarrio]);

  const selectedBoundaryId = useMemo(() => {
    const feature = findBoundaryFeature(boundaryGeojson, selectedBarrio || {});
    return (
      normalizeBoundaryId(feature?.properties?.boundaryId || feature?.id) ||
      normalizeBoundaryId(selectedBarrio?.boundaryId)
    );
  }, [boundaryGeojson, selectedBarrio]);

  const isSelectedBarrio = (row) => {
    if (!selectedBarrio) return false;
    if (row.boundaryId && selectedBarrio.boundaryId) {
      return normalizeBoundaryId(row.boundaryId) === normalizeBoundaryId(selectedBarrio.boundaryId);
    }
    return (
      String(row.neighborhood || "").toLowerCase() === String(selectedBarrio.neighborhood || "").toLowerCase() &&
      String(row.municipality || "").toLowerCase() === String(selectedBarrio.municipality || "").toLowerCase()
    );
  };

  return (
    <div className="cm-partner-market-tool">
      <header className="partner-dashboard-hero">
        <div>
          <p className="cm-partner-market-tool__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="partner-dashboard-hero__intro">{t(`${COPY}.subtitle`)}</p>
        </div>
        <div className="partner-dashboard-hero__actions">
          <Link className="partner-dashboard-btn" to={path("market")}>
            {t(`${COPY}.back`)}
          </Link>
          <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("market/brief")}>
            {t(`${COPY}.openBrief`)}
          </Link>
        </div>
      </header>

      <section className="partner-panel cm-partner-market-tool__controls">
        <label>
          <span>{t(`${COPY}.municipality`)}</span>
          <select value={municipality} onChange={(e) => setMunicipality(e.target.value)}>
            <option value="">{t(`${COPY}.allMunicipalities`)}</option>
            {(areas?.municipalities || []).map((row) => (
              <option key={row.name || row.slug} value={row.name}>{row.name}</option>
            ))}
          </select>
        </label>
        <button type="button" className="partner-dashboard-btn" onClick={load} disabled={loading}>
          {t(`${COPY}.refresh`)}
        </button>
      </section>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {!loading && heatmap ? (
        <div className="cm-partner-heatmap__layout">
          <section className="partner-panel cm-partner-heatmap__map-panel">
            <p className="muted">
              {t(`${COPY}.window`, { days: heatmap.windowDays })} · {t(`${COPY}.signalHint`)}
              {polygonCount
                ? ` · ${t(`${COPY}.polygonCount`, { count: polygonCount })}`
                : ` · ${t(`${COPY}.noPolygons`)}`}
            </p>
            <div className="cm-partner-heatmap__map">
              <ClientViewListingsMap
                key={`heatmap-${municipality || "all"}-${heatmap.generatedAt || ""}`}
                slug={CASAMAPA_CLIENT_VIEW_SLUG}
                listings={[]}
                boundaryGeojson={boundaryGeojson}
                selectedBoundaryId={selectedBoundaryId}
                onBoundarySelect={setSelectedBarrio}
                center={CASAMAPA_DEFAULT_CENTER}
                zoom={CASAMAPA_DEFAULT_ZOOM}
                initialBounds={initialBounds}
                fitToListings={false}
                allowEmptyMap
                height={420}
                ariaLabel={t(`${COPY}.mapAria`)}
                showMapControls
              />
            </div>
          </section>

          <section className="partner-panel cm-partner-heatmap__list-panel">
            <h2>{t(`${COPY}.rankingTitle`)}</h2>
            {barrios.length ? (
              <ol className="cm-partner-heatmap__list">
                {barrios.map((row) => {
                  const selected = isSelectedBarrio(row);
                  return (
                    <li
                      key={row.boundaryId || `${row.municipality}:${row.neighborhood}`}
                      className={selected ? "is-selected" : undefined}
                    >
                      <button
                        type="button"
                        className={`cm-partner-heatmap__pick${selected ? " is-selected" : ""}`}
                        aria-pressed={selected}
                        onClick={() =>
                          setSelectedBarrio((current) =>
                            isSelectedBarrio(row) && current
                              ? null
                              : {
                                  boundaryId: row.boundaryId || null,
                                  neighborhood: row.neighborhood,
                                  municipality: row.municipality,
                                }
                          )
                        }
                      >
                        <div className="cm-partner-heatmap__row">
                          <div>
                            <strong>{row.neighborhood}</strong>
                            <span className="muted"> · {row.municipality}</span>
                          </div>
                          <div className="cm-partner-heatmap__stats">
                            <span>{row.listingViews} {t(`${COPY}.views`)}</span>
                            <span>{row.inquiries} {t(`${COPY}.inquiries`)}</span>
                          </div>
                        </div>
                        <div className="cm-partner-heatmap__bar" aria-hidden="true">
                          <div style={{ width: `${Math.max(6, row.intensity)}%` }} />
                        </div>
                      </button>
                      <Link
                        className="cm-partner-heatmap__brief-link"
                        to={`${path("market/brief")}?municipality=${encodeURIComponent(row.municipality || "")}&neighborhood=${encodeURIComponent(row.neighborhood || "")}${row.boundaryId ? `&boundaryId=${encodeURIComponent(row.boundaryId)}` : ""}`}
                      >
                        {t(`${COPY}.briefLink`)}
                      </Link>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="muted">{t(`${COPY}.empty`)}</p>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
